import styled, {css} from 'styled-components';

import type {WithThemeProp} from '../../../styles';
import type {GetComponentProps} from '../../../utils/utility-types';
import type {StyledProps} from './types';

import {getColor} from '../../../styles';
import {Text} from '../../Text';

type TextStyledProps = Pick<
  StyledProps,
  'accentColor' | 'disabled' | 'isPressed' | 'scheme' | 'size'
> & {
  buttonVariant: StyledProps['variant'];
};

/**
 * ATTENTION! You can find a few ts-ignore comments in this file.
 *
 * It's a dirty hack to support web hover and native pressed state.
 * Text component itself has the variant prop which conflicts with the button's variant prop.
 * We use hover styles defined here in button styles for web hover
 * and here for native pressed state.
 */

const getSchemeColor =
  (
    scheme: TextStyledProps['scheme'],
    accentColor: TextStyledProps['accentColor'],
  ) =>
  <ComponentProps extends WithThemeProp>(p: ComponentProps) => {
    if (accentColor) {
      return getColor(accentColor)(p);
    }

    switch (scheme) {
      case 'attention':
        return getColor('text.error')(p);
      case 'primary':
      case 'secondary':
        return getColor(`text.${scheme}`)(p);
      default:
        return getColor('text.white')(p);
    }
  };

const getBaseColor = (p: TextStyledProps & WithThemeProp) => {
  // @ts-expect-error: TODO: describy why
  switch (p.buttonVariant || p.variant) {
    case 'primary':
      return getColor('text.white')(p);
    case 'secondary':
    case 'tertiary':
      return getColor('text.black')(p);
    default:
      return getSchemeColor(p.scheme, p.accentColor)(p);
  }
};

export const hoverColor = css<StyledProps>`
  color: ${p => {
    if (p.disabled) {
      return undefined;
    }

    // @ts-expect-error: TODO: describy why
    switch (p.buttonVariant || p.variant) {
      case 'outlined':
        return getColor('text.white')(p);
      case 'secondary':
      case 'tertiary':
        return getSchemeColor(p.scheme, p.accentColor)(p);
      default:
        // @ts-expect-error: TODO: describy why
        return getBaseColor(p);
    }
  }};
`;

const baseColor = css<TextStyledProps>`
  color: ${getBaseColor};

  ${p =>
    p.isPressed &&
    css`
      ${hoverColor};
    `}

  ${p =>
    p.disabled &&
    css`
      color: ${() => {
        switch (p.buttonVariant) {
          case 'primary':
            return getColor('text.white')(p);
          default:
            return getColor('text.gray')(p);
        }
      }};
    `}
`;

const transition = css`
  transition: all ease 0.2s;
`;

export const ButtonText = styled(Text).attrs<TextStyledProps>(p => {
  let variant: GetComponentProps<typeof Text>['variant'] = 'body';

  if (p.size === 'sm') {
    variant = 'label.la';
  }

  return {
    nativeProps: {
      numberOfLines: 1,
    },
    variant,
    weight: 'semibold',
  };
})<TextStyledProps>`
  user-select: none;
  ${transition}
  ${baseColor}
  /* ellipsis */
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: break-word;
  white-space: nowrap;
`;
